/* eslint-disable camelcase */
import { put } from 'redux-saga/effects';
import axios from 'src/api/Api';
import { errorHandler } from 'src/errorHandler/errorHandler';
import { logUiErrorToBackend } from 'src/utils/logUiErrorToBackend';
import {
    GET_SURGERY_DETAILS_SUCCESS,
    GET_SURGERY_DETAILS_FAILURE,
    getDropdownValues,
} from '../actions';
import {
    setSurgeryDetails,
    setCartData,
    setShippingDetailsEditMode,
    setProgressBarStateEditMode,
    validateProducts,
} from 'src/pages/equity/redux/actions';
import { handleEquityNotes } from 'src/common/redux/actions';
import { accountNumberZeroTrimmer, generate_UUID } from 'src/utils/util';
import { convertObjectToQueryParams } from 'src/utils/util';
import { calculateDiscountPercentage } from '../../components/common/utils/eventHandlerUtil';
import { getTotalExtendedPrice } from '../../components/common/utils/eventHandlerUtil';
import { EQUITYORDERTYPE } from '../../components/common/constants';
import i18n from 'src/i18n.js';

const getCopiedExtPrice = (item = {}) => {
    if (item?.extPrice != null) {
        return String(item.extPrice);
    }
    if (item?.unitPrice == null) {
        return undefined;
    }
    const unitPrice = parseFloat(item.unitPrice);
    const quantity = parseFloat(item?.quantity || 0);
    if (Number.isNaN(unitPrice)) {
        return undefined;
    }
    return (unitPrice * quantity).toFixed(2);
};

export const transformOrderHistoryData = (apiData) => {
    const orderBasicDetails = apiData?.orderBasicDetails || {};
    const orderSummaryDetail = orderBasicDetails?.orderSummaryDetail || {};
    const territoryDetails = orderSummaryDetail?.derivedTerritoryDetails?.[0] || {};
    const [contactName, contactNumber] = (orderSummaryDetail?.contact || '').split('/').map(str => str.trim());
    // The territories/districts dropdowns are keyed by the UID (districtCode ===
    // districtUID, terrirotryCode === territoryUID). The summary's districtCode
    // ("331") is the short code, which the territories API can't filter on and
    // returns nothing, blanking the field. Use the UID like the surgery copy flow.
    const summaryDistrictUID =
        orderSummaryDetail?.districtUID ||
        orderBasicDetails?.districtUID ||
        orderSummaryDetail?.districtCode ||
        '';
    const summaryTerritoryUID =
        orderSummaryDetail?.territoryUID ||
        orderSummaryDetail?.territoryCode ||
        territoryDetails?.territory ||
        '';

    return {
        accountDetails: {
            accountId: orderBasicDetails?.accountNumber || '',
            accountName: orderBasicDetails?.accountName || '',
            districtId: summaryDistrictUID,
            districtCode: orderSummaryDetail?.districtCode || '',
            districtName: orderSummaryDetail?.districtName || '',
            territoryId: summaryTerritoryUID,
            territoryCode: territoryDetails?.territory || '',
            territoryName: territoryDetails?.territoryName || '',
            patientContactName: contactName || '',
            patientContactNumber: contactNumber || '',
        },
        cartData: {
            products: (() => {
                // Drop invalid lines that are not real orderable products (e.g. the
                // "Customer Freight Fee" line comes back with approval === 'UNAPPROVED'
                // and no unit price). These can't be added via product search and must
                // not be copied into the cart or they inflate the count and are rejected
                // by the backend on submit.
                const isUnapproved = (item) =>
                    (item?.approval || '').toString().toUpperCase() === 'UNAPPROVED';
                const unapprovedCFNs = new Set(
                    (apiData?.orderDetails || [])
                        .filter(isUnapproved)
                        .map((od) => od?.cfn)
                );
                const usagePricingList = (apiData?.billingSummary?.usagePricing || []).filter(
                    (item) =>
                        (item?.cfn || '').toString().trim().length > 0 &&
                        !isUnapproved(item) &&
                        !unapprovedCFNs.has(item?.cfn)
                );
                const orderDetailsList = (apiData?.orderDetails || []).filter(
                    (item) =>
                        (item?.cfn || '').toString().trim().length > 0 &&
                        !isUnapproved(item)
                );
                if (usagePricingList.length > 0) {
                    return usagePricingList.map((pricing) => {
                        const orderDetail = orderDetailsList.find(
                            (od) => od?.cfn === pricing?.cfn
                        ) || {};
                        return {
                            productCFN: pricing?.cfn,
                            cfn: pricing?.cfn,
                            description: pricing?.description || orderDetail?.description,
                            quantity: pricing?.quantity,
                            salesOrg: pricing?.salesOrg || orderDetail?.salesOrg,
                            unitPrice: pricing?.unitPrice,
                            basePrice: pricing?.unitPrice != null ? String(pricing.unitPrice) : '0',
                            extPrice: getCopiedExtPrice(pricing),
                            // Keep the source price override when one was applied. When no
                            // override exists, leave overriddenUnitPrice undefined (not '') so
                            // the derived price fields stay undefined and are dropped by the JSON
                            // clone at submit. An empty string would survive the clone and be
                            // rejected by the backend as "Price format is invalid."
                            overriddenUnitPrice: pricing?.overridenUnitPrice || undefined,
                            uom: pricing?.uom || orderDetail?.uom,
                            isSet: orderDetail?.isSet ?? false,
                            implantable: orderDetail?.implantable ?? false,
                            instrument: orderDetail?.instrument ?? false,
                        };
                    });
                }
                return orderDetailsList.map((orderDetail) => ({
                    productCFN: orderDetail?.cfn,
                    cfn: orderDetail?.cfn,
                    description: orderDetail?.description,
                    quantity: orderDetail?.quantity,
                    salesOrg: orderDetail?.salesOrg,
                    unitPrice: orderDetail?.unitPrice,
                    basePrice: orderDetail?.unitPrice != null ? String(orderDetail.unitPrice) : '0',
                    extPrice: getCopiedExtPrice(orderDetail),
                    // Keep the source price override when one was applied. When no
                    // override exists, leave overriddenUnitPrice undefined (not '') so
                    // the derived price fields stay undefined and are dropped by the JSON
                    // clone at submit. An empty string would survive the clone and be
                    // rejected by the backend as "Price format is invalid."
                    overriddenUnitPrice: orderDetail?.overridenUnitPrice || undefined,
                    uom: orderDetail?.uom,
                    isSet: orderDetail?.isSet ?? false,
                    implantable: orderDetail?.implantable ?? false,
                    instrument: orderDetail?.instrument ?? false,
                }));
            })(),
            pricingNotes: '',
            overriddenReason: '',
        },
        shippingDetails: (orderBasicDetails?.shippingDetails || []).map((shipping) => ({
            ...shipping,
            poNumber: '',
            payment: {
                ...shipping?.payment,
                poNumber: '',
            },
        })),
        isFromUsage: false,
        parentMyCaseRef: null,
        equityCustomerNotes: '',
    };
};

export default function* getSurgeryDetails(action) {
    const progressBarState = {
        Account: {
            label: 'Account',
            displayLabel: i18n.t('equity.progressBar.account'),
            progress: 0,
            current: true,
            done: false,
        },
        Cart: {
            label: 'Cart',
            displayLabel: i18n.t('equity.progressBar.cart'),
            progress: 50,
            current: false,
            done: false,
        },
        Shipping: {
            label: 'Shipping',
            displayLabel: i18n.t('equity.progressBar.shipping'),
            progress: 0,
            current: false,
            done: false,
        },
        Submit: {
            label: 'Submit',
            displayLabel: i18n.t('equity.progressBar.submit'),
            progress: 0,
            current: false,
            done: false,
        },
    };
    const {
        myCaseRef,
        updateState,
        resourceType,
        isFromOrderHistory,
        isCopyFromSummary,
    } = action.payload;
    const useHistoryHydration = isFromOrderHistory || isCopyFromSummary;
    const objParams = {
        myCaseRef: myCaseRef,
        resourceType: resourceType,
    };

    const isFalsyValueRequired = false;
    let url = `/occ/v2/${localStorage.getItem(
        'baseSiteId'
    )}/order/equity?${convertObjectToQueryParams(
        objParams,
        isFalsyValueRequired
    )}`;

    try {
        let result;
        if (useHistoryHydration) {
            const histParams = convertObjectToQueryParams({ myCaseRef, resourceType: 'ALL' });
            const summaryPath = isFromOrderHistory
                ? 'orders/orderHistorySummary'
                : 'orders/summary';
            const histUrl = `/occ/v2/${localStorage.getItem('baseSiteId')}/${summaryPath}?${histParams}`;
            const histData = yield axios.get(histUrl).then((res) => res.data);
            result = transformOrderHistoryData(histData);
        } else {
            result = yield axios.get(url).then((response) => response.data);
        }
        const {
            accountDetails,
            cartData,
            shippingDetails,
            isFromUsage,
            parentMyCaseRef,
            equityCustomerNotes,
        } = result;
        if (accountDetails) {
            if (useHistoryHydration) {
                yield put(
                    getDropdownValues({
                        dropDownName: 'districts',
                    })
                );
            }

        const accountName = accountDetails?.accountName || '';
        const trimmedAccountName = accountName.trim();
        const cleanAccountName = useHistoryHydration
            ? (trimmedAccountName.endsWith('.') ||
                  trimmedAccountName.endsWith('!') ||
                  trimmedAccountName.endsWith('?')
                  ? trimmedAccountName.slice(0, -1)
                  : trimmedAccountName)
            : accountName;

        const accountIdData = {
                accountId: accountDetails.accountId,
                accountName: cleanAccountName,
                accountFormattedName:
                    cleanAccountName +
                    accountNumberZeroTrimmer(accountDetails.accountId),
            };
            //delete surgeryDetails.accountName;
            // delete accountDetails.accountName;
            delete accountDetails.districtCode;
            delete accountDetails.districtName;
            delete accountDetails.lastModifiedBy;
            delete accountDetails.lastModifiedDate;
            delete accountDetails.territoryName;
            delete accountDetails.territoryCode;
            // getAccountsParams = {
            //     districtId: accountDetails.districtId,
            //     territoryId: accountDetails.territoryId,
            // };
            // accountsUrl += `/aardt/accounts?${convertObjectToQueryParams(
            //     getAccountsParams,
            //     isFalsyValueRequired
            // )}`;
            // let accountResult = yield axios
            //     .get(accountsUrl)
            //     .then((response) => response.data);
            yield put(
                getDropdownValues({
                    dropDownName: 'territories',
                    // Copy flow: filter by the district UID so the single
                    // post-summary call returns this order's territory. Other
                    // flows keep the existing unfiltered behaviour.
                    ...(useHistoryHydration
                        ? { value: accountDetails.districtId }
                        : {}),
                    districtId: accountDetails.districtId,
                })
            );
            yield put(
                getDropdownValues({
                    dropDownName: 'accounts',
                    value: {
                        districtId: accountDetails.districtId,
                        territoryId: accountDetails.territoryId,
                    },
                    districtId: accountDetails.districtId,
                    territoryId: accountDetails.territoryId,
                })
            );
            accountDetails['accountId'] = accountIdData;
        }

        if (equityCustomerNotes) {
            let tempNote = [{ equityNotes: equityCustomerNotes }];
            yield put(handleEquityNotes(tempNote));
        }

        const pricingNotes = cartData && cartData?.pricingNotes;
        const overriddenReason = cartData && cartData?.overriddenReason;
        var cartProducts =
            cartData.products &&
            cartData.products.map((product) => ({
                ...product,
                productCFN: product.productCFN,
                quantity: +product.quantity,
                batchCurrentLocation: product?.batchAllocation?.currentLocation,
                batchId: product?.batchAllocation?.id,
                batchParked: product?.batchAllocation?.isParked,
                productKey: generate_UUID(),
                discount: product?.overriddenUnitPrice
                    ? calculateDiscountPercentage(
                          parseFloat(product?.unitPrice),
                          product?.overriddenUnitPrice &&
                              parseFloat(product?.overriddenUnitPrice)
                              ? parseFloat(product?.overriddenUnitPrice)
                              : 0
                      )
                    : product?.overriddenUnitPrice,
                adjustedPrice: product?.overriddenUnitPrice,
                adjustedUnitPrice: product?.overriddenUnitPrice,
                adjustedExtPrice: product?.overriddenUnitPrice
                    ? getTotalExtendedPrice(
                          product?.overriddenUnitPrice,
                          product?.quantity,
                          EQUITYORDERTYPE
                      )
                    : product?.overriddenUnitPrice,
                overriddenUnitPriceApplied: product?.overriddenUnitPrice
                    ? true
                    : false,
            }));
        // yield put(disableSurgeryDate(false));
        const bundleChildItems = cartProducts?.filter(
            (item) => item.parentCFN?.length > 0
        );
        cartProducts = cartProducts
            ?.filter((x) => !x.parentCFN)
            ?.map((product) => {
                const productObj = { ...product };
                if (
                    product.productCFN &&
                    (product.productCFN.toUpperCase().startsWith('M2') ||
                        product.productCFN.toUpperCase().startsWith('BUN') ||
                        product.productCFN.toUpperCase().startsWith('KEN') ||
                        product.productCFN.toUpperCase().startsWith('M1')) &&
                    bundleChildItems?.length > 0
                ) {
                    productObj.children = bundleChildItems?.filter(
                        (child) => child.parentCFN === product.productCFN
                    );
                    productObj.materialType = 'ZKMT';
                }
                return productObj;
            });

        const shippingObject = {
            S030: {
                shippingMethodId: '',
                shippingAddressId: '',
                attention: '',
                salesOrg: '',
                userAcknowledgement: false,
                payment: {
                    poAttachment: '',
                    poNumber: '',
                },
            },
            S002: {
                shippingMethodId: '',
                shippingAddressId: '',
                attention: '',
                salesOrg: '',
                userAcknowledgement: false,
                payment: {
                    poAttachment: '',
                    poNumber: '',
                },
            },
        };
        if (useHistoryHydration && shippingDetails?.length > 0) {
            // For order-history copy, avoid additional shipping lookups that can force a hard reload
            // via global auth interceptors. Keep shipping pre-population from summary payload only.
            shippingDetails?.forEach((shipping) => {
                const salesOrg =
                    shipping?.salesOrg === 'S030' || shipping?.salesOrg === 'S002'
                        ? shipping?.salesOrg
                        : 'S002';

                shippingObject[salesOrg] = {
                    shippingMethodId: shipping?.method?.id || '',
                    shippingAddressId: shipping?.shippingAddressId || '',
                    attention: shipping?.attention || shipping?.attn || '',
                    salesOrg,
                    userAcknowledgement: !!shipping?.userAcknowledgement,
                    payment: {
                        poAttachment: shipping?.payment?.poAttachment || '',
                        poNumber: '',
                    },
                };
            });
        } else if (shippingDetails) {
            shippingDetails?.forEach((shipping) => {
                if (shipping?.method?.id && shipping?.shippingAddressId) {
                    // if (shipping?.method?.id) {
                    shippingObject[shipping?.salesOrg === 'S030' || 
                        shipping?.salesOrg === 'S002'
                        ? shipping?.salesOrg
                        : 'S002'] = {
                        shippingMethodId: shipping?.method?.id,
                        shippingAddressId: shipping?.shippingAddressId,
                        attention: shipping?.attention,
                        salesOrg: shipping?.salesOrg,
                        userAcknowledgement: shipping?.userAcknowledgement,
                        payment: {
                            poAttachment: shipping?.payment?.poAttachment,
                            poNumber: shipping?.payment?.poNumber,
                        },
                    };
                }
            });
        }

        if (cartData?.length > 0 && shippingDetails) {
            progressBarState['Cart'].progress = 100;
            progressBarState['Cart'].done = true;
            progressBarState['Shipping'].progress = 100;
            progressBarState['Shipping'].done = true;
            progressBarState['Shipping'].current = false;
            progressBarState['Submit'].progress = 100;
            progressBarState['Account'].current = true;
            progressBarState['Account'].done = true;
        } else if (accountDetails && cartData?.products?.length > 0) {
            progressBarState['Cart'].progress = 100;
            progressBarState['Cart'].done = true;
            progressBarState['Cart'].current = false;
            progressBarState['Shipping'].progress = 100;
            progressBarState['Shipping'].current = false;
            progressBarState['Shipping'].done = false;
            progressBarState['Submit'].progress = 50;
            progressBarState['Account'].current = true;
            progressBarState['Account'].done = true;
        } else {
            progressBarState['Cart'].progress = 100;
            progressBarState['Cart'].done = false;
            progressBarState['Cart'].current = false;
            progressBarState['Shipping'].progress = 50;
            progressBarState['Shipping'].current = false;
            progressBarState['Shipping'].done = false;
            progressBarState['Submit'].progress = 0;
            progressBarState['Account'].current = true;
            progressBarState['Account'].done = true;
        }
        if (isFromOrderHistory || isCopyFromSummary) {
            // Copy creates a brand-new order, so the progress bar must start
            // fresh (only Account current, nothing done). The branches above
            // pre-mark Cart/Shipping/Submit as done because the copied order
            // already has products; reset them so future steps aren't shown as
            // completed while the user is still on the Account page.
            progressBarState['Account'].current = true;
            progressBarState['Account'].done = false;
            progressBarState['Account'].progress = 0;
            progressBarState['Cart'].current = false;
            progressBarState['Cart'].done = false;
            progressBarState['Cart'].progress = 50;
            progressBarState['Shipping'].current = false;
            progressBarState['Shipping'].done = false;
            progressBarState['Shipping'].progress = 0;
            progressBarState['Submit'].current = false;
            progressBarState['Submit'].done = false;
            progressBarState['Submit'].progress = 0;
        }
        if (updateState) {
            yield put(setSurgeryDetails(accountDetails));
            yield put(
                setCartData({
                    products: cartProducts,
                    pricingNotes: pricingNotes,
                    overriddenReason: overriddenReason,
                    getPricesForItems: cartProducts,
                    isFromUsage: isFromUsage,
                    isFromOrderHistory: isFromOrderHistory,
                    isCopyFromSummary: isCopyFromSummary,
                    reportUsageMyCaseRef: parentMyCaseRef,
                })
            );
            yield put(setShippingDetailsEditMode(shippingObject));
            // yield put(handleSurgeryNotes(notes || []));
            // yield put(
            //     changeAcknowlegmentCheckbox({
            //         name: 'pricingNotes',
            //         value: pricingNotes ? pricingNotes : '',
            //     })
            // );
            yield put(setProgressBarStateEditMode(progressBarState));
            // Copy flows (order history / order listing) pre-fill the cart with
            // the source order's products WITHOUT the normal add-to-cart
            // validation. Validate them right after hydration so any invalid
            // items (e.g. 'Invalid Product CFN') are dropped and never shown on
            // the cart page. `validateOnly` reconciles the cart in place without
            // re-adding the already-present copied items.
            if (isFromOrderHistory || isCopyFromSummary) {
                const productsForValidation = [
                    ...cartProducts.filter(
                        (p) => p.materialType !== 'ZKMT' && p.quantity > 0
                    ),
                    ...cartProducts.filter(
                        (p) =>
                            p.materialType === 'ZKMT' &&
                            p.quantity > 0 &&
                            p.children &&
                            p.children.length > 0
                    ),
                ];
                if (productsForValidation.length > 0) {
                    yield put(
                        validateProducts({
                            products: productsForValidation,
                            orderType: 'ZOR',
                            validateOnly: true,
                        })
                    );
                }
            }
        }
        yield put({ type: GET_SURGERY_DETAILS_SUCCESS, payload: result });
    } catch (e) {
        logUiErrorToBackend(e, {
            apiEndPoint: url,
            requestPayload: action.payload
        });
        yield put({
            type: GET_SURGERY_DETAILS_FAILURE,
            payload: errorHandler('503'),
        });
    }
}
